import React, { useEffect } from 'react';
import { FormDetailModel } from 'types/FormTypes';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import SearchableDropDown from 'components/atomic/SearchableDropDown';
import FormImage from './FormImage';
import envVars from 'env-vars';

const API_URL =
  envVars.REACT_APP_STAND_ALONE_MODE === 'false'
    ? envVars.REACT_APP_API_URL
    : '';

type FormSearchableDropDownProps = {
  formRef: FormikProps<any> | null;
  formData: FormDetailModel;
  name: string;
  index: number;
  formId: string;
  updateFormField: (name: string, value: string) => void;
};

const FormSearchableDropDown: React.FC<FormSearchableDropDownProps> = ({
  name,
  index,
  formId,
  formRef,
  formData,
  updateFormField,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'drawers.formDrawer.searchableDropDown',
  });

  useEffect(() => {
    formRef?.validateForm();
  }, []);

  const fetchServerRecords = async (query: string) => {
    try {
      const endpoint = formData?.config?.listId
        ? `${API_URL}/app_api/searchable/search-list`
        : `${API_URL}/app_api/searchable/search`;

      const payload = formData?.config?.listId
        ? {
            listId: formData.config.listId,
            value: query,
            limit: 100,
            sortingSchema: formData.config?.sortingSchema || 'date',
          }
        : {
            searchQuery: query,
            formId: formId,
            formIndex: index,
            limit: 100,
            sortingSchema: formData?.config?.sortingSchema || 'date',
          };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching server results:', error);
      return [];
    }
  };

  return (
    <div className='w-full h-full flex flex-col justify-start px-3 overflow-auto'>
      {formData.image && (
        <FormImage image={formData.image} alt='form-question-image' />
      )}
      <h5
        tabIndex={0}
        className={`text-left text-lg leading-formTitle font-semibold text-black ${
          !formData.subText ? 'mb-3' : 'mb-0'
        }`}
      >
        {formData.text}
      </h5>
      {formData.subText && (
        <p
          tabIndex={0}
          className='mt-1.5 mb-3 text-left text-xs font-semibold leading-formSubtitle text-muted'
        >
          {formData.subText}
        </p>
      )}
      <div className='w-full flex pb-40 flex-col'>
        {formRef && (
          <SearchableDropDown
            type='text'
            name='drop-down'
            allowedCustomValue={formData?.config?.allowedCustomValue}
            placeholder={formRef.values[name] ? '' : t('selectText')}
            selected={{
              value: formRef.values[name],
              label: formRef.values[name],
            }}
            fetchRecords={fetchServerRecords}
            options={formData?.options.map((option) => ({
              value: option.text,
              label: option.text,
            }))}
            onChange={async (option) => {
              await updateFormField(name, option.value);
            }}
          />
        )}
        <div className='mb-2'>
          {formRef?.errors[name] && formRef?.values[name] && (
            <span className='text-warning text-[0.8rem]'>
              {formRef.errors[name] as string}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormSearchableDropDown;
